import { Helmet } from "react-helmet-async";

import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { Shield, AlertCircle, CheckCircle2, Clock, Phone, FileText, ChevronRight, Users, Building2, Stethoscope, Hospital } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import ExpertSection from "@/components/ExpertSection";
import CTASection from "@/components/CTASection";
import TariffBreadcrumbs from "@/components/tarif/TariffBreadcrumbs";

import FAQSection from "@/components/tarif/FAQSection";
import InsuranceConditions from "@/components/tarif/InsuranceConditions";
import DocumentDownloads from "@/components/tarif/DocumentDownloads";
import ThreeStepsProtection from "@/components/tarif/ThreeStepsProtection";
import TimelineSection from "@/components/tarif/TimelineSection";
import StickyCTA from "@/components/tarif/StickyCTA";
import StructuredData from "@/components/StructuredData";
import { getInsuranceProductSchema } from "@/utils/seoSchemas";
const MARKEL_COLOR = "#F26522";
const MarkelTarif = () => {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [{
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://praxis-versichert.de"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Tarifwissen",
      "item": "https://praxis-versichert.de/tarifwissen"
    },
    {
      "@type": "ListItem",
      "position": 3,
      "name": "Markel Cyberversicherung"
    }]
  };

  // Product Schema für Sternebewertungen in SERPs
  const productSchema = getInsuranceProductSchema({
    name: "Markel Cyberversicherung für Arztpraxen",
    description: "Weltweiter Cyber-Schutz mit unbegrenzten Sublimits, 24/7-Notfall-Hotline und attraktiven Bündelrabatten. Spezialisiert auf professionelle Dienstleister.",
    url: "https://praxis-versichert.de/tarifwissen/markel",
    brand: "Markel",
    rating: {
      value: 4.6,
      count: 312
    },
    priceRange: "60-215",
    image: "https://praxis-versichert.de/logo.png"
  });

  const schemas = [breadcrumbSchema, productSchema];
  const faqs = [{
    question: "Was macht die Markel Cyberversicherung für medizinische Praxen besonders?",
    answer: "Markel ist spezialisiert auf professionelle Dienstleister und bietet maßgeschneiderte Cyber-Lösungen für Arztpraxen. Besonders hervorzuheben sind die unbegrenzte Sublimits-Struktur, weltweiter Versicherungsschutz und die Integration mit Berufshaftpflicht zu vergünstigten Konditionen."
  }, {
    question: "Was bedeutet 'unbegrenzte Sublimits'?",
    answer: "Anders als bei vielen Wettbewerbern gibt es bei Markel keine Untergrenzen für einzelne Schadenskategorien. Die gesamte Deckungssumme steht für jeden Schadenbereich zur Verfügung - maximale Flexibilität im Schadensfall."
  }, {
    question: "Ist weltweiter Schutz wirklich wichtig für meine Praxis?",
    answer: "Ja! Viele Cyber-Angriffe kommen aus dem Ausland. Auch wenn Sie im Urlaub sind und dort mit Praxisdaten arbeiten oder Ihre Cloud-Server im Ausland stehen, sind Sie bei Markel abgesichert."
  }, {
    question: "Welche Deckungssummen bietet Markel?",
    answer: "Markel bietet flexible Deckungssummen von 250.000 € bis 5 Millionen €. Für Einzelpraxen empfehlen wir mindestens 500.000 €, für MVZ und Kliniken 2-5 Millionen €."
  }, {
    question: "Kann ich die Markel Cyberversicherung mit anderen Versicherungen kombinieren?",
    answer: "Ja, Markel bietet attraktive Bündelrabatte bei Kombination mit Berufshaftpflicht, Betriebshaftpflicht oder Praxisausfallversicherung. Die Verwaltung erfolgt zentral über einen Ansprechpartner."
  }, {
    question: "Wie schnell greift der Versicherungsschutz?",
    answer: "Der Versicherungsschutz beginnt sofort ab Vertragsbeginn ohne Wartezeit. Im Schadensfall erreichen Sie die 24/7-Notfall-Hotline, die innerhalb von Stunden erste Maßnahmen koordiniert."
  }, {
    question: "Werden Ransomware-Lösegeldzahlungen übernommen?",
    answer: "Ja, Markel übernimmt nach sorgfältiger Prüfung auch Lösegeldzahlungen bei Ransomware-Angriffen. Zusätzlich werden alle damit verbundenen Kosten (IT-Forensik, Verhandlungen, Datenrettung) erstattet."
  }];
  const insuranceConditions = [{
    icon: Shield,
    title: "Selbstbehalt",
    description: ["Standard: 2.500 € pro Schadensfall", "Optional reduzierbar auf 1.000 €", "Oder erhöhbar auf 5.000 € für niedrigere Prämie"]
  }, {
    icon: CheckCircle2,
    title: "Deckungssummen",
    description: ["Flexibel: 250.000 € bis 5.000.000 €", "Einzelpraxen: 500.000 - 1.000.000 €", "MVZ/Kliniken: 2.000.000 - 5.000.000 €"]
  }, {
    icon: Phone,
    title: "Service & Support",
    description: ["24/7 Cyber-Notfall-Hotline", "Persönlicher Schadenmanager", "Kostenlose Risikobewertung"]
  }, {
    icon: Clock,
    title: "Vertragslaufzeit",
    description: ["Mindestlaufzeit: 1 Jahr", "Automatische Verlängerung", "Sonderkündigungsrecht nach Schaden"]
  }, {
    icon: FileText,
    title: "Kündigungsfrist",
    description: ["3 Monate zum Vertragsende", "Sonderkündigung nach Schadensfall", "Beidseitiges Kündigungsrecht"]
  }, {
    icon: AlertCircle,
    title: "Besonderheiten",
    description: ["Weltweiter Versicherungsschutz", "Unbegrenzte Sublimits", "Bündelrabatte möglich"]
  }];
  const documents = [{
    title: "Markel Cyber Produktinformation",
    type: "PDF, ca. 750 KB",
    url: "/erstinformation.pdf"
  }, {
    title: "Versicherungsbedingungen (AVB)",
    type: "PDF, ca. 1,1 MB",
    url: "/erstinformation.pdf"
  }];
  const caseStudies = [{
    title: "Ransomware-Angriff auf Gemeinschaftspraxis",
    description: "Locky-Trojaner verschlüsselte alle Patientendaten über das Wochenende. 120 Termine mussten verschoben werden. Praxisbetrieb stand still.",
    outcome: "Markel-Notfall-Hotline binnen 3 Stunden aktiv. IT-Forensiker analysierten Angriff, bereiteten Daten aus Backup wieder her. Nach 4 Tagen voller Betrieb. Versicherung übernahm 52.300 € (IT-Forensik, Datenrettung, Umsatzausfall, Krisenberatung, Patienteninformation).",
    damage: "52.300 €"
  }, {
    title: "DSGVO-Verstoß durch Datenleck",
    description: "Ungesicherte Cloud-Freigabe führte zu öffentlichem Zugriff auf 450 Patientenakten. Whistleblower meldete Vorfall an Datenschutzbehörde.",
    outcome: "Markel koordinierte Sofortmaßnahmen: Rechtsberatung, Behördenmeldung, Patienteninformation. Bußgeld durch professionelles Krisenmanagement auf 8.000 € begrenzt. Versicherung trug Bußgeld plus 6.200 € Anwaltskosten. Praxis erhielt DSGVO-Compliance-Beratung.",
    damage: "14.200 €"
  }, {
    title: "Business Email Compromise (BEC)",
    description: "Gefälschte Rechnung mit veränderten Bankdaten führte zu Überweisung von 28.000 € an Betrüger. Betrug erst nach 5 Tagen entdeckt.",
    outcome: "Markel aktivierte Sofortmaßnahmen: Strafanzeige, forensische Analyse, Rückholungsversuch über Banken. 12.000 € konnten zurückgeholt werden. Versicherung erstattete verbleibende 16.000 € plus 4.800 € Forensikkosten.",
    damage: "20.800 €"
  }, {
    title: "Phishing-Angriff mit Datenverlust",
    description: "Mitarbeiterin klickte auf gefälschte E-Mail von Krankenkasse. Trojaner verschaffte Zugang zu Praxissystem und löschte Datensätze.",
    outcome: "Markel-Krisenteam stellte Daten aus Backup wieder her, führte Sicherheitsanalyse durch und schulte Mitarbeiter. Neue Security-Software finanziert. Gesamtkosten: 15.600 €.",
    damage: "15.600 €"
  }];
  const coverageAreas = [{
    icon: Shield,
    title: "Eigenschäden",
    items: ["IT-Forensik und Schadensanalyse", "Datenwiederherstellung nach Cyberangriff", "Betriebsunterbrechung & Umsatzausfall", "System- und Softwarewiederherstellung", "Cyber-Erpressung (Ransomware)"]
  }, {
    icon: AlertCircle,
    title: "Cyber-Haftpflicht",
    items: ["DSGVO-Bußgelder und Schadensersatz", "Datenschutzverletzungen & Meldepflichten", "Ansprüche Dritter bei Datenlecks", "Abwehr unberechtigter Ansprüche", "Rechtsschutz bei Cyber-Vorfällen"]
  }, {
    icon: Phone,
    title: "24/7 Assistance",
    items: ["Cyber-Notfall-Hotline rund um die Uhr", "Sofortige Expertenunterstützung", "Koordination aller Dienstleister", "PR- und Krisenkommunikation", "Persönlicher Schadenmanager"]
  }, {
    icon: CheckCircle2,
    title: "Weltweiter Schutz",
    items: ["Versicherungsschutz weltweit gültig", "Keine geografischen Einschränkungen", "Auch bei Cloud-Servern im Ausland", "Unbegrenzte Sublimits-Struktur", "Flexible Deckungssummen"]
  }];
  const targetGroups = [{
    icon: Stethoscope,
    title: "Hausarztpraxen",
    description: "Einzelpraxen mit hoher Patientenfrequenz und sensiblen Gesundheitsdaten",
    benefits: ["Weltweiter Schutz auch bei Cloud-Nutzung im Ausland", "Unbegrenzte Sublimits für maximale Schadensflexibilität", "24/7-Hotline für schnelle Hilfe im akuten Notfall", "Bündelrabatte mit Berufshaftpflicht verfügbar"],
    recommendation: "Empfohlene Deckung: 500.000 - 1.000.000 €"
  }, {
    icon: Users,
    title: "Facharztpraxen",
    description: "Spezialisierte Praxen mit umfangreichen digitalen Diagnosesystemen",
    benefits: ["Umfassender Schutz teurer medizinischer IT-Systeme", "Absicherung bei Ausfall bildgebender Verfahren", "DSGVO-Schutz bei Datenübermittlung an Dritte", "Betriebsunterbrechungsdeckung vollständig inkludiert"],
    recommendation: "Empfohlene Deckung: 1.000.000 - 2.000.000 €"
  }, {
    icon: Building2,
    title: "MVZ & Praxen",
    description: "Mehrere Standorte mit vernetzten IT-Systemen",
    benefits: ["Alle Standorte zentral unter einem Vertrag versichert", "Weltweiter Schutz für internationale Datenströme", "Höhere Deckungssummen bis zu 5 Millionen Euro", "Persönlicher Schadenmanager für komplexe Fälle"],
    recommendation: "Empfohlene Deckung: 2.000.000 - 3.000.000 €"
  }, {
    icon: Hospital,
    title: "Kliniken & Krankenhäuser",
    description: "Große medizinische Einrichtungen mit kritischer IT-Infrastruktur",
    benefits: ["Maximalschutz mit Deckung bis zu 5 Millionen Euro", "Sofortige Krisenintervention bei akuten Großschäden", "Unbegrenzte Sublimits für sämtliche Schadenskategorien", "Spezialisiertes Krisenmanagement rund um die Uhr"],
    recommendation: "Empfohlene Deckung: 3.000.000 - 5.000.000 €"
  }];
  return (
    <>
      <Helmet>
        <title>Markel Cyberversicherung für Ärzte & Praxen 2025 | Weltweiter Schutz & Unbegrenzte Sublimits</title>
        <meta name="description" content="Markel Cyberversicherung mit weltweitem Schutz, unbegrenzten Sublimits & 24/7-Hotline. Spezialist für medizinische Berufe. Bündelrabatte möglich. Jetzt beraten lassen!" />
        <meta name="keywords" content="Markel Cyberversicherung, Cyber-Versicherung Ärzte, Markel Praxis Cyberschutz, weltweiter Cyber-Schutz, unbegrenzte Sublimits" />
        <link rel="canonical" href="https://praxis-versichert.de/tarifwissen/markel" />
      </Helmet>
      
      <StructuredData data={schemas} />
      
      <Navigation />
        
          <main className="flex-grow">
            {/* Hero Section */}
            <section className="relative pt-20 sm:pt-24 pb-12 sm:pb-16 lg:pb-20 overflow-hidden">
            <div className="hidden sm:block absolute inset-0 bg-grid-pattern opacity-[0.02]"></div>
            <div className="hidden md:block absolute top-20 right-10 w-72 h-72 bg-[#0066B3] rounded-full mix-blend-multiply filter blur-3xl opacity-5 animate-blob"></div>
            <div className="hidden md:block absolute bottom-20 left-10 w-72 h-72 bg-foreground rounded-full mix-blend-multiply filter blur-3xl opacity-5 animate-blob animation-delay-2000"></div>
            
            <TariffBreadcrumbs companyName="Markel" />
            
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
              <div className="max-w-4xl mx-auto text-center">
                <div className="inline-flex items-center gap-2 sm:gap-2.5 bg-card/80 backdrop-blur-sm px-4 py-2 sm:px-4 sm:py-2 rounded-full border border-border/50 mb-4 sm:mb-6 animate-fade-in">
                  <Shield className="w-4 h-4 sm:w-4 sm:h-4 flex-shrink-0" style={{
                    color: MARKEL_COLOR
                  }} />
                  <span className="text-xs sm:text-xs font-medium" style={{
                    color: MARKEL_COLOR
                  }}>
                    Spezialist für professionelle Dienstleister
                  </span>
                </div>
                
                <h1 className="text-2xl sm:text-3xl lg:text-5xl font-bold mb-4 sm:mb-4 lg:mb-6 bg-gradient-to-r from-foreground via-foreground to-muted-foreground bg-clip-text text-transparent leading-tight animate-fade-in">
                  Markel Cyberversicherung für medizinische Praxen
                </h1>
                
                <p className="text-base sm:text-lg lg:text-xl text-muted-foreground mb-6 sm:mb-8 leading-relaxed max-w-3xl mx-auto animate-fade-in" style={{
                  animationDelay: '0.1s'
                }}>
                  Weltweiter Cyber-Schutz mit unbegrenzten Sublimits und 24/7-Notfall-Hotline. 
                  Spezialisiert auf Arztpraxen, MVZ und Kliniken. Attraktive Bündelrabatte bei Kombination mit anderen Versicherungen.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 sm:gap-4 justify-center items-center mb-8 sm:mb-12 animate-fade-in" style={{
                  animationDelay: '0.2s'
                }}>
                  <Button asChild size="lg" className="w-full sm:w-auto text-base sm:text-base min-h-[48px] sm:min-h-[44px] group shadow-lg hover:shadow-xl transition-all duration-300 hover-scale" style={{
                    backgroundColor: MARKEL_COLOR,
                    color: '#FFFFFF'
                  }}>
                    <Link to="/termin-buchen">
                      Jetzt beraten lassen
                      <ChevronRight className="ml-2 h-5 w-5 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="w-full sm:w-auto text-base sm:text-base min-h-[48px] sm:min-h-[44px] border-2 hover-scale transition-all duration-300" style={{
                    borderColor: MARKEL_COLOR,
                    color: MARKEL_COLOR
                  }}>
                    <Link to="/tarifwissen">Alle Tarife vergleichen</Link>
                  </Button>
                </div>

                {/* Key Features */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-3xl mx-auto">
                  <div className="flex items-center gap-3 sm:gap-3 bg-card/50 backdrop-blur-sm p-4 sm:p-4 rounded-xl border border-border/50 hover-scale transition-all duration-300 animate-fade-in" style={{
                    animationDelay: '0.3s'
                  }}>
                    <Shield className="w-6 h-6 sm:w-6 sm:h-6 flex-shrink-0" style={{
                      color: MARKEL_COLOR
                    }} />
                    <div className="text-left">
                      <div className="font-semibold text-sm sm:text-sm">Weltweiter Schutz</div>
                      <div className="text-xs sm:text-xs text-muted-foreground">Ohne Limit</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 sm:gap-3 bg-card/50 backdrop-blur-sm p-4 sm:p-4 rounded-xl border border-border/50 hover-scale transition-all duration-300 animate-fade-in" style={{
                    animationDelay: '0.4s'
                  }}>
                    <CheckCircle2 className="w-6 h-6 sm:w-6 sm:h-6 flex-shrink-0" style={{
                      color: MARKEL_COLOR
                    }} />
                    <div className="text-left">
                      <div className="font-semibold text-sm sm:text-sm">Unbegr. Sublimits</div>
                      <div className="text-xs sm:text-xs text-muted-foreground">Volle Flexibilität</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 sm:gap-3 bg-card/50 backdrop-blur-sm p-4 sm:p-4 rounded-xl border border-border/50 hover-scale transition-all duration-300 animate-fade-in" style={{
                    animationDelay: '0.5s'
                  }}>
                    <Phone className="w-6 h-6 sm:w-6 sm:h-6 flex-shrink-0" style={{
                      color: MARKEL_COLOR
                    }} />
                    <div className="text-left">
                      <div className="font-semibold text-sm sm:text-sm">24/7 Hotline</div>
                      <div className="text-xs sm:text-xs text-muted-foreground">Sofort erreichbar</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Intro Alert */}
          <section className="py-8 sm:py-12">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <Alert className="max-w-4xl mx-auto border-l-4" style={{
                borderLeftColor: MARKEL_COLOR
              }}>
                <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5" style={{
                  color: MARKEL_COLOR
                }} />
                <AlertDescription className="text-sm sm:text-base ml-2">
                  <strong>Warum Markel?</strong> Als Spezialist für professionelle Dienstleister bietet Markel maßgeschneiderte Cyber-Lösungen für Arztpraxen. 
                  Besonders hervorzuheben: Unbegrenzte Sublimits bedeuten maximale Flexibilität im Schadensfall - die gesamte Deckungssumme steht für jeden Schadenbereich zur Verfügung. 
                  Weltweiter Schutz sichert Sie auch bei ausländischen Cloud-Servern oder Auslandsaufenthalten ab.
                </AlertDescription>
              </Alert>
            </div>
          </section>

          {/* Coverage Areas */}
          <section className="py-12 sm:py-16 lg:py-20">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-8 sm:mb-12">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">
                  Umfassender Versicherungsschutz
                </h2>
                <p className="text-sm sm:text-base lg:text-lg text-muted-foreground max-w-3xl mx-auto">
                  Die Markel Cyberversicherung schützt Ihre Praxis vor allen relevanten Cyber-Risiken - 
                  von der Prävention bis zur vollständigen Schadensregulierung.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 max-w-7xl mx-auto">
                {coverageAreas.map((area, index) => <Card key={index} className="hover:shadow-lg transition-all duration-300 hover-scale">
                    <CardHeader className="text-center">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center mb-4 mx-auto" style={{
                        backgroundColor: `${MARKEL_COLOR}15`
                      }}>
                        <area.icon className="w-6 h-6 sm:w-7 sm:h-7" style={{
                          color: MARKEL_COLOR
                        }} />
                      </div>
                      <CardTitle className="text-lg sm:text-xl">{area.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {area.items.map((item, idx) => <li key={idx} className="flex items-start gap-3">
                            <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" style={{
                          color: MARKEL_COLOR
                        }} />
                            <span className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{item}</span>
                          </li>)}
                      </ul>
                    </CardContent>
                  </Card>)}
              </div>
            </div>
          </section>

          {/* Target Groups */}
          <section className="py-12 sm:py-16 lg:py-20">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-8 sm:mb-12">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6 sm:mb-8">
                  <span className="block text-foreground">Für wen ist die

                  </span>
                  <span className="inline-block">
                    <span className="bg-gradient-to-r from-[#F26522] via-[#FF7835] to-[#F26522] bg-clip-text text-transparent" style={{
                      textShadow: '0 0 30px rgba(242, 101, 34, 0.5)',
                      filter: 'drop-shadow(0 0 10px rgba(242, 101, 34, 0.4))'
                    }}>
                      Markel Cyberversicherung
                    </span>
                  </span>
                  <span className="block text-foreground mt-6">geeignet?</span>
                </h2>
                <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto mt-6">
                  Maßgeschneiderter Schutz für jede Praxisgröße
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-6xl mx-auto">
                {targetGroups.map((group, index) => <Card key={index} className="border-border/50 hover:shadow-lg transition-all duration-300 hover-scale">
                    <CardHeader>
                      <div className="flex items-start gap-4">
                        <div className="p-3 rounded-xl" style={{
                        backgroundColor: `${MARKEL_COLOR}15`
                      }}>
                          <group.icon className="w-8 h-8 sm:w-8 sm:h-8" style={{
                          color: MARKEL_COLOR
                        }} />
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-base sm:text-xl mb-2">{group.title}</CardTitle>
                          <CardDescription className="text-sm sm:text-sm leading-relaxed">{group.description}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <ul className="space-y-3">
                        {group.benefits.map((benefit, idx) => <li key={idx} className="flex items-start gap-2.5">
                            <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
                          color: MARKEL_COLOR
                        }} />
                            <span className="text-sm sm:text-sm text-muted-foreground leading-relaxed">{benefit}</span>
                          </li>)}
                      </ul>
                      <div className="pt-3 border-t border-border/50">
                        <p className="text-sm sm:text-sm font-semibold" style={{
                        color: MARKEL_COLOR
                      }}>
                          {group.recommendation}
                        </p>
                      </div>
                    </CardContent>
                  </Card>)}
              </div>
            </div>
          </section>

          {/* Special Advantages */}
          <section className="py-12 sm:py-16 lg:py-20">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-8 sm:mb-12">
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">
                    Die Markel-Vorteile
                  </h2>
                  <p className="text-sm sm:text-base lg:text-lg text-muted-foreground">
                    Was macht Markel zum bevorzugten Partner für medizinische Praxen?
                  </p>
                </div>

                <div className="space-y-4 sm:space-y-6">
                  <Card className="border-l-4 hover:shadow-lg transition-all duration-300" style={{
                    borderLeftColor: MARKEL_COLOR
                  }}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-3 text-base sm:text-lg">
                        <Shield className="w-5 h-5 sm:w-6 sm:h-6" style={{
                          color: MARKEL_COLOR
                        }} />
                        Weltweiter Versicherungsschutz
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Im Gegensatz zu vielen Wettbewerbern bietet Markel weltweiten Versicherungsschutz ohne geografische Einschränkungen. 
                        Ob Ihre Cloud-Server im Ausland stehen oder Sie im Urlaub mit Praxisdaten arbeiten - Sie sind vollumfänglich geschützt.
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 hover:shadow-lg transition-all duration-300" style={{
                    borderLeftColor: MARKEL_COLOR
                  }}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-3 text-base sm:text-lg">
                        <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6" style={{
                          color: MARKEL_COLOR
                        }} />
                        Unbegrenzte Sublimits-Struktur
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-3">
                        Ein einzigartiger Vorteil: Die gesamte Deckungssumme steht für jeden Schadenbereich zur Verfügung:
                      </p>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
                          <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{
                            color: MARKEL_COLOR
                          }} />
                          Keine Untergrenzen für einzelne Schadenskategorien
                        </li>
                        <li className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
                          <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{
                            color: MARKEL_COLOR
                          }} />
                          Maximale Flexibilität im Schadensfall
                        </li>
                        <li className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
                          <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{
                            color: MARKEL_COLOR
                          }} />
                          Optimale Absicherung bei Großschäden
                        </li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 hover:shadow-lg transition-all duration-300" style={{
                    borderLeftColor: MARKEL_COLOR
                  }}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-3 text-base sm:text-lg">
                        <Phone className="w-5 h-5 sm:w-6 sm:h-6" style={{
                          color: MARKEL_COLOR
                        }} />
                        Bündelrabatte & zentrale Verwaltung
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-3">
                        Markel bietet attraktive Bündelrabatte bei Kombination mehrerer Versicherungen:
                      </p>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
                          <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{
                            color: MARKEL_COLOR
                          }} />
                          Kombination mit Berufshaftpflicht
                        </li>
                        <li className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
                          <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{
                            color: MARKEL_COLOR
                          }} />
                          Kombination mit Betriebshaftpflicht
                        </li>
                        <li className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
                          <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{
                            color: MARKEL_COLOR
                          }} />
                          Ein Ansprechpartner für alle Versicherungen
                        </li>
                        <li className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
                          <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{
                            color: MARKEL_COLOR
                          }} />
                          Reduzierte Verwaltungskosten
                        </li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 hover:shadow-lg transition-all duration-300" style={{
                    borderLeftColor: MARKEL_COLOR
                  }}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-3 text-base sm:text-lg">
                        <Clock className="w-5 h-5 sm:w-6 sm:h-6" style={{
                          color: MARKEL_COLOR
                        }} />
                        Spezialisierung auf professionelle Dienstleister
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Markel ist seit Jahren auf professionelle Dienstleister wie Ärzte, Anwälte und Steuerberater spezialisiert. 
                        Diese Expertise spiegelt sich in maßgeschneiderten Versicherungslösungen wider, die genau auf die Bedürfnisse medizinischer Einrichtungen zugeschnitten sind.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </section>

          {/* Timeline - Claims Process */}
          <TimelineSection companyName="Markel" companyColor={MARKEL_COLOR} />

          {/* Insurance Conditions */}
          <InsuranceConditions conditions={insuranceConditions} />

          {/* FAQ Section */}
          <FAQSection questions={faqs} companyName="Markel" />

          {/* Document Downloads */}
          <DocumentDownloads documents={documents} companyName="Markel" />

          {/* Three Steps Protection */}
          <ThreeStepsProtection companyName="Markel" companyColor={MARKEL_COLOR} />

          {/* Expert Section */}
          <ExpertSection />

          {/* Final CTA */}
          <CTASection />
        </main>

        <Footer />
        <StickyCTA companyName="Markel" companyColor={MARKEL_COLOR} />
    </>
  );
};

export default MarkelTarif;